import React, { useEffect, Suspense } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
// Import the error boundary
import ErrorBoundary, { RouteErrorElement } from './ErrorBoundary';
import { lazy } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import md from "./middleware/middleware"
import { GlobalDebug } from './middleware/debugConsole';
import { useIntercom } from 'react-use-intercom';
import { useSelector } from "react-redux";
import "./app.css"


const lazyWithRetry = (componentImport: any) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem(
        'page-has-been-force-refreshed'
      ) || 'false'
    );

    try {
      const component = await componentImport();

      window.localStorage.setItem(
        'page-has-been-force-refreshed',
        'false'
      );

      return component;
    } catch (error) {
      console.log(error);
      ///*
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Assuming that the user is not on the latest version of the application.
        // Let's refresh the page immediately.
        caches.keys().then((names) => {
          names.forEach((name) => {
            caches.delete(name);
          });
        });
        window.localStorage.setItem(
          'page-has-been-force-refreshed',
          'true'
        );
        return window.location.reload();
      }

      // The page has already been reloaded
      // Assuming that user is already using the latest version of the application.
      // Let's let the application crash and raise the error.
      throw error;
      //*/
    }
  });

const Home = lazyWithRetry(() => import('./components/Home/Home'));
const Login = lazyWithRetry(() => import('./components/Oauth/Login'));
const Oauth = lazyWithRetry(() => import('./components/Oauth/Oauth'));
const Review = lazyWithRetry(() => import('./components/Review/Review'));
const ReviewHome = lazyWithRetry(() => import('./components/Review/ReviewHome'));
const SideBar = lazyWithRetry(() => import('./components/SideBar/SideBar'));
const Station = lazyWithRetry(() => import('./components/Station/Station'));
const Wrapper = lazyWithRetry(() => import('./components/Wrapper'));
const Stations = lazyWithRetry(() => import('./components/ChiefStations/Stations'));
const Account = lazyWithRetry(() => import('./components/Account/Account'));
const PrivacyPolicy = lazyWithRetry(() => import('./components/Privacy/PrivacyPolicy'));
const Stats = lazyWithRetry(() => import('./components/Stats/Stats'));
const ResetPassword = lazyWithRetry(() => import('./components/Oauth/ResetPassword'));
const UpdatePassword = lazyWithRetry(() => import('./components/Oauth/UpdatePassword'));
const About = lazyWithRetry(() => import('./components/About/About'));
const SectorDetails = lazyWithRetry(() => import('./components/SectorDetails/SectorDetails'));
const StationDetails = lazyWithRetry(() => import('./components/StationDetails/StationDetails'));
const ReviewsDetails = lazyWithRetry(() => import('./components/ReviewsDetails/ReviewsDetails'));
const ManageReview = lazyWithRetry(() => import('./components/ManageReviews/ManageReviews'));
const CanalDetails = lazyWithRetry(() => import('./components/CanalDetails/CanalDetails'));
const EmptyReview = lazyWithRetry(() => import('./components/Review/EmptyReview/EmptyReview'));
const UserList = lazyWithRetry(() => import('./components/UserList/UserList'));
const Maintenance = lazyWithRetry(() => import('./components/Maintenance/Maintenance'));
const ForgotPassword = lazyWithRetry(() => import('./components/Oauth/ForgotPassword'));
const SimulateChunkLoadError = lazyWithRetry(() => import('./tests/SimulateChunkLoadError'));


declare global {
  interface Window {
    Intercom: any;
  }
}

// Même règle d'accès qu'avant : store Redux en priorité, sinon localStorage.
const RequireAuth = () => {
  const user = useSelector((state: any) => state.user);
  const globalUser = user.globalUserEmail !== null ? user : JSON.parse(window.localStorage.getItem('user') || '{}');
  const isCgu = globalUser ? globalUser.cguAccteptance : false;
  return isCgu ? <Outlet /> : <Navigate to="/login" replace />;
};

const NotFoundRedirect = () => {
  const user = useSelector((state: any) => state.user);
  if (user && user.cguAccteptance) {
    return <Navigate to="/" replace />;
  } else {
    return <Navigate to="/login" replace />;
  }
};

const routerChildElements = [
  {
    path: '',
    element: <Home />,
  },
  {
    path: "/review",
    element: <ReviewHome key={"review"} />
  },
  {
    path: "/conso/review",
    element: <ReviewHome key={"review-conso"} />
  },
  {
    path: '/review/conso/accounts/:accountId/locations/:locationId/reviews/:reviewId/:index',
    element: <Review />
  },
  {
    path: '/review/accounts/:accountId/locations/:locationId/reviews/:reviewId/:index',
    element: <Review />
  },
  {
    path: '/review/no_comment/accounts/:accountId/locations/:locationId/reviews/:reviewId/:index',
    element: <EmptyReview />
  },
  {
    path: '/notification',
    element: <SideBar />
  },
  {
    path: "/station",
    element: <Station />
  },
  {
    path: "/stations",
    element: <Stations />
  },
  {
    path: "/stations/:filter",
    element: <Stations />
  },
  {
    path: "/account",
    element: <Account />
  },
  {
    path: "/stats",
    element: <Stats />
  },
  {
    path: "/about",
    element: <About />
  },
  {
    path: "/sectors-details",
    element: <SectorDetails />
  },
  {
    path: "/station-details",
    element: <StationDetails />
  },
  {
    path: "/reviews-details",
    element: <ReviewsDetails />
  },
  {
    path: "/manage-reviews",
    element: <ManageReview />
  },
  {
    path: "/canal-details",
    element: <CanalDetails />
  },
  {
    path: "/user-list",
    element: <UserList />
  }
];


// Routeur créé une seule fois : ne doit jamais être recréé au rendu de App,
// sinon tout l'arbre est démonté/remonté (ex. au login), ce qui fait planter
// React quand le DOM a été modifié par un traducteur ou une extension.
const router = createBrowserRouter([
  {
    path: "/",
    element: <RequireAuth />,
    errorElement: <RouteErrorElement />,
    children: [
      {
        path: "/",
        element: <Wrapper />,
        children: [
          {
            path: "/",
            element: <SideBar />,
            children: routerChildElements,
          }
        ]
      }
    ]
  },
  {
    path: "/privacy-policy",
    element: <PrivacyPolicy />,
    errorElement: <RouteErrorElement />
  },
  {
    path: "/login",
    element: <Login />,
    errorElement: <RouteErrorElement />
  },
  {
    path: "/forgotPassword",
    element: <ForgotPassword />,
    errorElement: <RouteErrorElement />
  },
  {
    path: "/oauth2callback/token/:token",
    element: < Oauth />,
    errorElement: <RouteErrorElement />
  },
  {
    path: "/maintenance",
    element: <Maintenance />,
    errorElement: <RouteErrorElement />
  },
  {
    path: "resetPassword",
    element: <ResetPassword />,
    errorElement: <RouteErrorElement />
  },
  {
    path: "updatePassword/:tmpPassword/:userEmail",
    element: < UpdatePassword />,
    errorElement: <RouteErrorElement />
  },
  {
    path: "*",
    element: <NotFoundRedirect />,
    errorElement: <RouteErrorElement />
  }
])

function App() {
  const { boot } = useIntercom();

  useEffect(() => {
    if (process.env.REACT_APP_URL === 'https://totalenergies.datakeen.co/backend') {
      GlobalDebug(false)
    }
    boot()
    // if (window.location.pathname !== "/maintenance") {
    // window.location.assign(`${window.location.origin}/maintenance`)
    // }
  }, [])

  return (
    <>
      <Suspense fallback={<div />}>
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </Suspense>
    </>
  );
}

export default App;
